/**
 * ChangePassword Component
 * 
 * Allows users to change their own password.
 * Requires current password for security.
 * 
 * @param onClose - Callback function to close the popup
 */
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import './ChangePassword.css';

interface ChangePasswordProps {
  onClose: () => void;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ onClose }) => {
  const { refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!currentPassword) {
      setError('Bitte geben Sie Ihr aktuelles Passwort ein');
      return;
    }

    if (!newPassword) {
      setError('Bitte geben Sie ein neues Passwort ein');
      return;
    }

    if (newPassword.length < 6) {
      setError('Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      // User changing their own password - use the dedicated endpoint
      const response = await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        currentPassword,
        newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        // Refresh user context to get updated passwordResetRequired flag
        await refreshUser();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-overlay" onClick={onClose}>
      <div className="change-password-container" onClick={(e) => e.stopPropagation()}>
        <div className="change-password-header">
          <h2 className="change-password-title">Passwort ändern</h2>
          <button className="change-password-close" onClick={onClose}>×</button>
        </div>

        {success ? (
          <div className="change-password-success">
            <p>✓ Passwort erfolgreich geändert!</p>
            <div className="change-password-actions" style={{ marginTop: '20px', justifyContent: 'center' }}>
              <button
                type="button"
                className="change-password-submit"
                onClick={onClose}
              >
                Schließen
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="change-password-form" autoComplete="off">
            <div className="change-password-form-group">
              <label htmlFor="current-password" className="change-password-label">
                Aktuelles Passwort
              </label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  e.target.setCustomValidity('');
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  (e.target as HTMLInputElement).setCustomValidity('Bitte füllen Sie dieses Feld aus');
                }}
                className="change-password-input"
                required
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            <div className="change-password-form-group">
              <label htmlFor="new-password" className="change-password-label">
                Neues Passwort
              </label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  e.target.setCustomValidity('');
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  const target = e.target as HTMLInputElement;
                  if (target.validity.valueMissing) {
                    target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                  } else if (target.validity.tooShort) {
                    target.setCustomValidity('Bitte geben Sie mindestens 6 Zeichen ein');
                  }
                }}
                className="change-password-input"
                required
                autoComplete="new-password"
                disabled={loading}
                minLength={6}
              />
              <small className="change-password-hint">Mindestens 6 Zeichen</small>
            </div>

            <div className="change-password-form-group">
              <label htmlFor="confirm-password" className="change-password-label">
                Neues Passwort bestätigen
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  e.target.setCustomValidity('');
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  const target = e.target as HTMLInputElement;
                  if (target.validity.valueMissing) {
                    target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                  } else if (target.validity.tooShort) {
                    target.setCustomValidity('Bitte geben Sie mindestens 6 Zeichen ein');
                  }
                }}
                className="change-password-input"
                required
                autoComplete="new-password"
                disabled={loading}
                minLength={6}
              />
            </div>

            {error && <div className="change-password-error">{error}</div>}

            <div className="change-password-actions">
              <button
                type="button"
                className="change-password-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="change-password-submit"
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? 'Wird geändert...' : 'Passwort ändern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;

